import { useEffect, useId, useRef } from 'react';
import { cn } from '@/shared/lib';
import { FieldMarker } from '@/shared/ui/field-label';
import type { CheckboxProps } from './types';
import { controlStyles, fieldStyles } from './styles';

export type { CheckboxProps } from './types';

/**
 * Builds the `aria-describedby` value for a field.
 *
 * Hint and error can both be present, and both belong in the description — a
 * validation message the user cannot hear is not an accessible error.
 */
function describedBy(fieldId: string, hint?: string, error?: string): string | undefined {
  const ids = [hint ? `${fieldId}-hint` : null, error ? `${fieldId}-error` : null].filter(
    (id): id is string => id !== null,
  );
  return ids.length > 0 ? ids.join(' ') : undefined;
}

/**
 * Labelled checkbox, keyboard operable, with an optional mixed state.
 *
 * Uses a native `<input type="checkbox">` inside a wrapping `<label>`, which
 * gets three things for free that a custom control has to re-derive: Space
 * toggles, the label is the accessible name AND the click target, and the
 * mixed state maps to `aria-checked="mixed"`.
 */
export function Checkbox({
  label,
  hint,
  error,
  required = false,
  optional = false,
  indeterminate = false,
  disabled = false,
  className,
  ...rest
}: CheckboxProps) {
  const generatedId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label
      className={cn(fieldStyles.root, disabled && fieldStyles.disabled, className)}
      data-disabled={disabled || undefined}
    >
      <input
        {...rest}
        ref={inputRef}
        type="checkbox"
        disabled={disabled}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-labelledby={generatedId}
        aria-describedby={describedBy(generatedId, hint, error)}
        className={controlStyles({ invalid: Boolean(error) })}
      />
      <span className={fieldStyles.text}>
        {/*
          The name is wired by id rather than left to the wrapping label.
          A label names a control by ALL of its text, so a hint sitting inside
          it would be announced as part of the name — "Include archived tenants
          Applies to list views." — and `aria-describedby` would then describe
          the control with text the name already contains.
        */}
        <span id={generatedId} className={fieldStyles.label}>
          {label}
        </span>
        {/*
          The marker sits OUTSIDE the id'd name span, so it is not announced as
          part of the name. The control's own `required` attribute is what tells
          assistive technology the field is mandatory.
        */}
        <FieldMarker required={required} optional={optional} />
        {hint ? (
          <span id={`${generatedId}-hint`} className={fieldStyles.hint}>
            {hint}
          </span>
        ) : null}
        {error ? (
          <span id={`${generatedId}-error`} className={fieldStyles.error}>
            {error}
          </span>
        ) : null}
      </span>
    </label>
  );
}